import { trpc } from '@web/lib/trpc';

import { toast } from 'sonner';

import { EditableText } from '@web/components/ui/editable-text';

export const ContactInfo = ({ contactId }: { contactId: string }) => {
  const { data: contactBasicInfo, refetch: refetchContactBasicInfo } =
    trpc.contact.getContactBasicInfo.useQuery({
      contactId,
    });

  const { data: contactDetails, refetch: refetchContactDetails } =
    trpc.contact.getContactDetails.useQuery({
      contactId,
    });

  const { mutateAsync: updateContactBasicInfo } =
    trpc.contact.updateContactBasicInfo.useMutation();

  const { mutateAsync: updateContactDetails } =
    trpc.contact.updateContactDetails.useMutation();

  const {
    data: contactBasicMedicalInfo,
    refetch: refetchContactBasicMedicalInfo,
  } = trpc.contact.getContactBasicMedicalInfo.useQuery({
    contactId,
  });

  const { mutateAsync: updateContactBasicMedicalInfo } =
    trpc.contact.updateContactBasicMedicalInfo.useMutation();

  const showToast = (details: Record<string, string | number | boolean>) => {
    const description = Object.entries(details)
      .map(([key, value]) => `${key}: ${value.toString()}`)
      .join('\n');

    toast.success('successfully updated', {
      description,
    });
  };

  const handleUpdateContactBasicInfo = async (
    details: Parameters<typeof updateContactBasicInfo>[0]['details'],
  ) => {
    await updateContactBasicInfo({
      details,
      contactId,
    });

    showToast(details);

    await refetchContactBasicInfo();
  };

  const handleUpdateContactDetails = async (
    details: Parameters<typeof updateContactDetails>[0]['details'],
  ) => {
    await updateContactDetails({
      details,
      contactId,
    });

    showToast(details);

    await refetchContactDetails();
  };

  const handleUpdateContactBasicMedicalInfo = async (
    details: Parameters<typeof updateContactBasicMedicalInfo>[0]['details'],
  ) => {
    await updateContactBasicMedicalInfo({
      details,
      contactId,
    });

    showToast(details);

    await refetchContactBasicMedicalInfo();
  };

  if (!contactBasicInfo || !contactDetails || !contactBasicMedicalInfo)
    return null;

  return (
    <div className="ml-2 grid grid-cols-2 items-center gap-y-0">
      <span className="w-max font-semibold">First Name</span>
      <EditableText
        initialText={contactBasicInfo.firstName}
        onSave={async (text) => {
          await handleUpdateContactBasicInfo({
            firstName: text,
          });
        }}
      />

      <span className="w-max font-semibold">Last Name</span>
      <EditableText
        initialText={contactBasicInfo.lastName}
        onSave={async (text) => {
          await handleUpdateContactBasicInfo({
            lastName: text,
          });
        }}
      />

      <span className="font-semibold">Phone</span>
      <EditableText
        initialText={contactBasicInfo.phoneNumber}
        onSave={async (text) => {
          await handleUpdateContactBasicInfo({
            phoneNumber: text,
          });
        }}
      />

      <span className="font-semibold">Email</span>
      <EditableText
        initialText={contactBasicInfo.email ?? ''}
        onSave={async (text) => {
          await handleUpdateContactBasicInfo({
            email: text,
          });
        }}
      />

      <span className="font-semibold">Address</span>
      <EditableText
        initialText={contactDetails.address ?? ''}
        onSave={async (text) => {
          await handleUpdateContactDetails({
            address: text,
          });
        }}
      />

      <span className="font-semibold">Height</span>
      <EditableText
        initialText={contactBasicMedicalInfo.height?.toString() ?? ''}
        append={'cm'}
        onSave={async (text) => {
          await handleUpdateContactBasicMedicalInfo({
            height: Number(text),
          });
        }}
      />

      <span className="font-semibold">Weight</span>
      <EditableText
        initialText={contactBasicMedicalInfo.weight?.toString() ?? ''}
        append={'kg'}
        onSave={async (text) => {
          await handleUpdateContactBasicMedicalInfo({
            weight: Number(text),
          });
        }}
      />

      <span className="font-semibold">Meds</span>
      <EditableText
        initialText={contactBasicMedicalInfo.medications ?? ''}
        onSave={async (text) => {
          await handleUpdateContactBasicMedicalInfo({
            medications: text,
          });
        }}
      />

      <span className="font-semibold">Diseases</span>
      <EditableText
        initialText={contactBasicMedicalInfo.diseases ?? ''}
        onSave={async (text) => {
          await handleUpdateContactBasicMedicalInfo({
            diseases: text,
          });
        }}
      />
    </div>
  );
};
